/**
 * React-Query hooks for the User Management page.
 *
 * Backend surface:
 *   GET   /api/admin/internal-users/                       paginated list (excludes customers)
 *   GET   /api/admin/internal-users/:id/                   detail (profile + nested + summary)
 *   PATCH /api/admin/internal-users/:id/                   edit core profile fields
 *   POST  /api/admin/internal-users/:id/reset-password/    one-time temp password
 *   GET   /api/bi/crm/users/:id/links/                     CRM rules + active assignments
 *
 * Access control summary for the page reuses the existing per-user
 * preview at:
 *   GET   /feature/bi/api/admin/preview/users/:id/         effective resource map
 */
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { apiClient, get } from '@/api/client';
import type { AccessResponse } from '@/api/bi/access';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface InternalUserRow {
  id: number;
  name: string | null;
  username: string | null;
  email: string | null;
  phone_number: string | null;
  role: string | null;
  role_display: string | null;
  is_active: boolean;
  is_staff: boolean;
  is_superuser: boolean;
  date_joined: string | null;
  last_active: string | null;
  last_login: string | null;
}

export interface InternalUserListResponse {
  results: InternalUserRow[];
  count?: number;
  total_items?: number;
  page?: number;
  has_next_page?: boolean;
  next?: string | null;
  previous?: string | null;
}

export interface StoreRef {
  id: number;
  name: string;
}

export interface EmployeeInfoNested {
  id: number;
  store: StoreRef | null;
  related_stores: (StoreRef | null)[];
  document_type: string;
  document_image: string | null;
  is_available: boolean;
  is_online: boolean;
  phone_number: string | null;
  working_hours: number | null;
}

export interface RiderInfoNested {
  id: number;
  store: StoreRef | null;
  document_type: string;
  document_image: string | null;
  emergency_contact_number: string | null;
  is_online: boolean;
  is_available: boolean;
  phone_number: string | null;
  uses_cycle: boolean;
  working_hours: number | null;
}

export interface UserInfoNested {
  id: number;
  name: string;
  address: string;
  additional_info: string | null;
  latitude: number | null;
  longitude: number | null;
}

export interface InternalUserSummary {
  tickets_created: number;
  tickets_resolved: number;
  tickets_currently_assigned: number;
  routing_rules_targeted: number;
}

export interface InternalUserDetail extends InternalUserRow {
  employee_info: EmployeeInfoNested | null;
  rider_info: RiderInfoNested | null;
  user_info: UserInfoNested[];
  summary: InternalUserSummary;
}

export interface InternalUserPatch {
  name?: string;
  email?: string | null;
  phone_number?: string | null;
  username?: string | null;
  role?: string | null;
  is_active?: boolean;
  is_staff?: boolean;
}

export interface ResetPasswordResponse {
  user_id: number;
  temporary_password: string;
}

/**
 * Roles selectable in the User Management Create form. Mirrors the
 * `ADMIN_STYLE_ROLES` allowlist on the backend — roles that need a
 * related profile row (rider/driver/staff/packer/manager/...) are
 * intentionally absent and continue to be created from the existing
 * Roster / Riders pages.
 */
export const CREATABLE_ADMIN_STYLE_ROLES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'admin', label: 'Admin' },
  { value: 'support', label: 'Support' },
  { value: 'operation', label: 'Operation' },
  { value: 'account', label: 'Account' },
  { value: 'audit', label: 'Audit' },
  { value: 'agency', label: 'Agency' },
  { value: 'vendor', label: 'Vendor' },
];

export interface InternalUserCreatePayload {
  name: string;
  username?: string | null;
  phone_number?: string | null;
  email?: string | null;
  role?: string | null;
  is_active?: boolean;
  is_staff?: boolean;
  is_superuser?: boolean;
  password?: string | null;
  generate_temp_password?: boolean;
}

/**
 * Successful create response: full detail payload + an optional
 * `temporary_password` field present only when the admin asked the
 * server to auto-generate one. Surface it in a one-shot modal — the
 * server does not persist the plaintext anywhere else.
 */
export interface InternalUserCreateResponse extends InternalUserDetail {
  temporary_password?: string;
}

// CRM links payload
export interface CrmRoutingTargetLink {
  id: number;
  rule: {
    id: number;
    name: string;
    is_enabled: boolean;
    evaluation_order: number;
  };
  tier: {
    id: number;
    position: number;
    label: string;
    wait_seconds_after_previous: number;
    notify_event_kind: string;
  };
  role: string | null;
  is_primary: boolean;
  is_watcher: boolean;
  flag: 'primary' | 'watcher' | 'member';
}

export interface CrmActiveAssignment {
  id: number;
  ticket: {
    id: number;
    type: string;
    status: string;
    priority: string;
    summary: string;
    created_at: string | null;
    updated_at: string | null;
  };
  is_primary: boolean;
  note: string;
  acknowledged_at: string | null;
  assigned_by: { id: number; name: string; phone: string | null; role: string | null } | null;
  created_at: string | null;
}

export interface CrmUserLinksResponse {
  user: { id: number; name: string; phone: string | null; role: string | null } | null;
  routing_targets: CrmRoutingTargetLink[];
  active_assignments: CrmActiveAssignment[];
  counts: {
    tickets_created: number;
    tickets_resolved: number;
    tickets_currently_assigned: number;
    routing_rules_targeted: number;
  };
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const KEY_LIST = ['internal-users', 'list'] as const;
const KEY_DETAIL = (id: number | null) => ['internal-users', 'detail', id] as const;
const KEY_CRM_LINKS = (id: number | null) => ['internal-users', 'crm-links', id] as const;
const KEY_ACCESS_PREVIEW = (id: number | null) =>
  ['internal-users', 'access-preview', id] as const;

// ---------------------------------------------------------------------------
// Filters helper
// ---------------------------------------------------------------------------

export interface InternalUserListParams {
  search?: string;
  role?: string;
  isActive?: boolean | null;
  page?: number;
  pageSize?: number;
}

function listParams(p: InternalUserListParams): Record<string, string | number> {
  const out: Record<string, string | number> = {
    page: p.page ?? 1,
    page_size: p.pageSize ?? 25,
  };
  if (p.search) out.search = p.search;
  if (p.role) out.role = p.role;
  if (p.isActive != null) out.is_active = p.isActive ? 'true' : 'false';
  return out;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useInternalUsers(p: InternalUserListParams) {
  return useQuery({
    queryKey: [
      ...KEY_LIST,
      p.search ?? '',
      p.role ?? '',
      p.isActive ?? null,
      p.page ?? 1,
      p.pageSize ?? 25,
    ],
    queryFn: () =>
      get<InternalUserListResponse>('/api/admin/internal-users/', { params: listParams(p) }),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useInternalUser(id: number | null) {
  return useQuery({
    queryKey: KEY_DETAIL(id),
    queryFn: () => get<InternalUserDetail>(`/api/admin/internal-users/${id}/`),
    enabled: id != null && id > 0,
    staleTime: 30_000,
  });
}

export function useUpdateInternalUser(id: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: InternalUserPatch) => {
      if (id == null) throw new Error('User id is required');
      const r = await apiClient.patch<InternalUserDetail>(
        `/api/admin/internal-users/${id}/`,
        patch,
      );
      return r.data;
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: KEY_DETAIL(id) }),
        qc.invalidateQueries({ queryKey: KEY_LIST }),
      ]);
    },
  });
}

export function useCreateInternalUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: InternalUserCreatePayload) => {
      const r = await apiClient.post<InternalUserCreateResponse>(
        '/api/admin/internal-users/',
        payload,
      );
      return r.data;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: KEY_LIST });
    },
  });
}

export function useResetUserPassword(id: number | null) {
  return useMutation({
    mutationFn: async () => {
      if (id == null) throw new Error('User id is required');
      const r = await apiClient.post<ResetPasswordResponse>(
        `/api/admin/internal-users/${id}/reset-password/`,
      );
      return r.data;
    },
  });
}

export function useUserCrmLinks(id: number | null) {
  return useQuery({
    queryKey: KEY_CRM_LINKS(id),
    queryFn: () => get<CrmUserLinksResponse>(`/api/bi/crm/users/${id}/links/`),
    enabled: id != null && id > 0,
    staleTime: 30_000,
  });
}

/**
 * Effective per-user BI access map. Reuses the existing admin preview
 * endpoint (superuser-only on the backend) — the User Management page
 * is gated to admin/superuser anyway, so this is safe to call here.
 */
export function useUserAccessPreview(id: number | null) {
  return useQuery({
    queryKey: KEY_ACCESS_PREVIEW(id),
    queryFn: () => get<AccessResponse>(`/feature/bi/api/admin/preview/users/${id}/`),
    enabled: id != null && id > 0,
    staleTime: 30_000,
    retry: 0,
  });
}
